import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useMatch } from 'react-router-dom';
import Header from '../../components/Header';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { ArrowLeft, AlertCircle, Bell, CalendarClock, FileText, Play, Save, Search } from 'lucide-react';
import { formatCurrencyDecimals } from '../../utils/formatters';
import { EmailTestModeBadge } from '../../components/revenue/EmailTestModeBadge';
import SingleSelectDropdown from '../../components/SingleSelectDropdown';

interface Hotel {
  id: number;
  title: string;
  /** From Backyou invoicing-contacts sync (hotels.invoicing_contact) */
  invoicing_contact: string | null;
}

interface HotelWithRevenue extends Hotel {
  estimated_revenue: number;
  groups_count: number;
}

interface EmailTemplate {
  id: string;
  name: string;
  type: string;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/** Stable A→Z order: trims titles; digits sort before letters (e.g. "1881…" before "Courtyard…"). */
function compareHotelTitle(
  a: string | null | undefined,
  b: string | null | undefined
): number {
  return (a ?? '')
    .trim()
    .localeCompare((b ?? '').trim(), 'en', { sensitivity: 'base', numeric: true });
}

/** Form controls — same look as Meeting Preparation (border, shadow, text-sm, blue focus ring). */
const MP_FIELD =
  'rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500';

/** Every wall-clock half hour, 00:00 … 23:30 (launch automation cadence). */
const SCHEDULE_LAUNCH_HALF_HOUR_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = (i % 2) * 30;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
});

/** Round wall-clock time to nearest :00 / :30 for editing legacy scheduled_at values. */
function snapDateToLocalHalfHour(d: Date): Date {
  const out = new Date(d.getTime());
  if (Number.isNaN(out.getTime())) return out;
  const totalMinutes = out.getHours() * 60 + out.getMinutes();
  const snappedTotal = Math.round(totalMinutes / 30) * 30;
  const h = Math.floor(snappedTotal / 60);
  const min = snappedTotal % 60;
  out.setHours(h, min, 0, 0);
  return out;
}

function datetimeLocalValueFromDate(d: Date): string {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

export default function CampaignSetup() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const editMatch = useMatch('/revenue-collection/campaigns/:id/edit');
  const editCampaignId = editMatch?.params.id ?? null;
  const isEditMode = !!editCampaignId;

  const currentYear = new Date().getFullYear();
  const [periodMonth, setPeriodMonth] = useState(new Date().getMonth() + 1);
  const [periodYear, setPeriodYear] = useState(currentYear);
  const [campaignName, setCampaignName] = useState('');
  const [hotelsWithRevenue, setHotelsWithRevenue] = useState<HotelWithRevenue[]>([]);
  const [selectedHotelIds, setSelectedHotelIds] = useState<Set<number>>(new Set());
  const [initialTemplates, setInitialTemplates] = useState<EmailTemplate[]>([]);
  const [selectedInitialTemplateId, setSelectedInitialTemplateId] = useState<string>('');
  const [reminderConfigs, setReminderConfigs] = useState<{ templateId: string; days: number }[]>([
    { templateId: '', days: 7 },
    { templateId: '', days: 14 },
    { templateId: '', days: 21 },
  ]);
  const [reminderTemplates, setReminderTemplates] = useState<EmailTemplate[]>([]);
  const [confirmationTemplates, setConfirmationTemplates] = useState<EmailTemplate[]>([]);
  const [selectedConfirmationTemplateId, setSelectedConfirmationTemplateId] = useState<string>('');
  const [scheduledLaunchDate, setScheduledLaunchDate] = useState('');
  /** HH:mm from SCHEDULE_LAUNCH_HALF_HOUR_OPTIONS; empty until user picks a slot. */
  const [scheduledLaunchTime, setScheduledLaunchTime] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingCampaign, setLoadingCampaign] = useState(!!editCampaignId);
  const [saving, setSaving] = useState(false);
  const [pendingSaveAction, setPendingSaveAction] = useState<'draft' | 'scheduled' | 'launch_now' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hotelSearchQuery, setHotelSearchQuery] = useState('');
  /** When true, hotel list shows only rows without invoicing contact (amber-highlighted). */
  const [missingContactFilter, setMissingContactFilter] = useState(false);

  useEffect(() => {
    if (!editCampaignId) {
      setLoadingCampaign(false);
      return;
    }
    let cancelled = false;
    async function loadCampaignForEdit() {
      setLoadingCampaign(true);
      setError(null);
      try {
        const { data: c, error: cErr } = await supabase
          .from('revenue_collection_campaigns')
          .select(
            'period_month, period_year, name, status, notification_template_id, confirmation_template_id, scheduled_at'
          )
          .eq('id', editCampaignId)
          .single();

        if (cancelled) return;
        if (cErr || !c) {
          setError('Campaign not found');
          navigate('/revenue-collection', { replace: true });
          return;
        }
        if (c.status !== 'draft' && c.status !== 'scheduled') {
          navigate(`/revenue-collection/campaigns/${editCampaignId}`, { replace: true });
          return;
        }

        setPeriodMonth(c.period_month);
        setPeriodYear(c.period_year);
        setCampaignName(c.name || '');
        setSelectedInitialTemplateId(c.notification_template_id || '');
        setSelectedConfirmationTemplateId(c.confirmation_template_id || '');

        if (c.scheduled_at) {
          const snapped = snapDateToLocalHalfHour(new Date(c.scheduled_at));
          const local = datetimeLocalValueFromDate(snapped);
          const sep = local.indexOf('T');
          const dPart = sep >= 0 ? local.slice(0, sep) : '';
          const tPart = sep >= 0 ? local.slice(sep + 1, sep + 6) : '';
          setScheduledLaunchDate(dPart);
          setScheduledLaunchTime(
            tPart && SCHEDULE_LAUNCH_HALF_HOUR_OPTIONS.includes(tPart) ? tPart : '',
          );
        } else {
          setScheduledLaunchDate('');
          setScheduledLaunchTime('');
        }

        const { data: ch } = await supabase
          .from('revenue_collection_campaign_hotels')
          .select('hotel_id')
          .eq('campaign_id', editCampaignId);

        if (cancelled) return;
        setSelectedHotelIds(new Set((ch || []).map((row) => row.hotel_id)));

        const { data: rcRows } = await supabase
          .from('revenue_collection_reminder_config')
          .select('reminder_template_id, days_after_initial, reminder_number')
          .eq('campaign_id', editCampaignId)
          .order('reminder_number');

        if (cancelled) return;
        const nextReminders = [
          { templateId: '', days: 7 },
          { templateId: '', days: 14 },
          { templateId: '', days: 21 },
        ];
        (rcRows || []).forEach((row) => {
          const i = row.reminder_number - 1;
          if (i >= 0 && i < 3) {
            nextReminders[i] = {
              templateId: row.reminder_template_id,
              days: row.days_after_initial,
            };
          }
        });
        setReminderConfigs(nextReminders);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load campaign');
        }
      } finally {
        if (!cancelled) {
          setLoadingCampaign(false);
        }
      }
    }
    loadCampaignForEdit();
    return () => {
      cancelled = true;
    };
  }, [editCampaignId, navigate]);

  // Fetch hotels with revenue for the period
  useEffect(() => {
    async function fetchHotels() {
      setLoading(true);
      try {
        const { data: groupData, error: groupErr } = await supabase
          .from('mv_group_enriched_summary')
          .select('hotel_id, estimated_revenue')
          .eq('arrival_month', periodMonth)
          .eq('arrival_year', periodYear)
          .in('status', ['Booked', 'reserved'])
          .gt('estimated_revenue', 0);

        if (groupErr) throw groupErr;

        const byHotel = new Map<number, { revenue: number; count: number }>();
        (groupData || []).forEach((r: { hotel_id: number | null; estimated_revenue: string | number }) => {
          const hid = r.hotel_id;
          if (hid == null) return;
          const rev = Number(r.estimated_revenue) || 0;
          const cur = byHotel.get(hid) || { revenue: 0, count: 0 };
          byHotel.set(hid, { revenue: cur.revenue + rev, count: cur.count + 1 });
        });

        const hotelIds = Array.from(byHotel.keys());
        if (hotelIds.length === 0) {
          setHotelsWithRevenue([]);
          setLoading(false);
          return;
        }

        const { data: hotelData, error: hotelErr } = await supabase
          .from('hotels')
          .select('id, title, invoicing_contact')
          .in('id', hotelIds);

        if (hotelErr) throw hotelErr;

        const list: HotelWithRevenue[] = (hotelData || []).map((h: Hotel) => {
          const agg = byHotel.get(h.id) || { revenue: 0, count: 0 };
          return {
            ...h,
            estimated_revenue: agg.revenue,
            groups_count: agg.count,
          };
        });
        setHotelsWithRevenue(list);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load hotels');
      } finally {
        setLoading(false);
      }
    }
    fetchHotels();
  }, [periodMonth, periodYear]);

  useEffect(() => {
    setHotelSearchQuery('');
    setMissingContactFilter(false);
  }, [periodMonth, periodYear]);

  // Fetch templates
  useEffect(() => {
    async function fetchTemplates() {
      const { data: initial } = await supabase
        .from('revenue_collection_email_templates')
        .select('id, name, type')
        .eq('type', 'initial');
      setInitialTemplates(initial || []);

      const { data: reminders } = await supabase
        .from('revenue_collection_email_templates')
        .select('id, name, type')
        .in('type', ['reminder_1', 'reminder_2', 'reminder_3']);
      setReminderTemplates(reminders || []);

      const { data: confirm } = await supabase
        .from('revenue_collection_email_templates')
        .select('id, name, type')
        .eq('type', 'confirmation');
      setConfirmationTemplates(confirm || []);

      if (!isEditMode) {
        if ((initial || []).length > 0 && !selectedInitialTemplateId) {
          setSelectedInitialTemplateId(initial![0].id);
        }
        if ((confirm || []).length > 0 && !selectedConfirmationTemplateId) {
          setSelectedConfirmationTemplateId(confirm![0].id);
        }
      }
    }
    fetchTemplates();
  }, [isEditMode]);

  const hotelsWithoutEmail = hotelsWithRevenue.filter(
    (h) => !h.invoicing_contact?.trim()
  );

  const hotelsSortedAlphabetically = useMemo(
    () => [...hotelsWithRevenue].sort((a, b) => compareHotelTitle(a.title, b.title)),
    [hotelsWithRevenue]
  );

  const hotelsFiltered = useMemo(() => {
    let list = hotelsSortedAlphabetically;
    if (missingContactFilter) {
      list = list.filter((h) => !h.invoicing_contact?.trim());
    }
    const q = hotelSearchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((h) => (h.title || '').toLowerCase().includes(q));
  }, [hotelsSortedAlphabetically, hotelSearchQuery, missingContactFilter]);

  const toggleHotel = (id: number) => {
    setSelectedHotelIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedHotelIds((prev) => {
      const next = new Set(prev);
      hotelsFiltered.forEach((h) => next.add(h.id));
      return next;
    });
  };

  const deselectAll = () => {
    setSelectedHotelIds(new Set());
  };

  const totalPeriodEstimated = hotelsWithRevenue.reduce((s, h) => s + h.estimated_revenue, 0);

  const totalEstimated = hotelsWithRevenue
    .filter((h) => selectedHotelIds.has(h.id))
    .reduce((s, h) => s + h.estimated_revenue, 0);

  const hotelsWithRevenueCount = hotelsWithRevenue.length;
  const hotelsSelectedCount = selectedHotelIds.size;

  const yearOptions = useMemo(() => {
    const set = new Set([currentYear - 1, currentYear, currentYear + 1, periodYear]);
    return Array.from(set).sort((a, b) => a - b);
  }, [currentYear, periodYear]);

  const handleSubmit = async (saveAction: 'draft' | 'scheduled' | 'launch_now') => {
    if (selectedHotelIds.size === 0) {
      setError('Select at least one hotel');
      return;
    }
    if (!selectedInitialTemplateId) {
      setError('Select an initial notification template');
      return;
    }
    if (!selectedConfirmationTemplateId) {
      setError('Select a confirmation template');
      return;
    }
    if (saveAction === 'scheduled' && (!scheduledLaunchDate || !scheduledLaunchTime)) {
      setError('Choose a date and time for Save and launch later');
      return;
    }

    setSaving(true);
    setPendingSaveAction(saveAction);
    setError(null);
    try {
      const status: 'draft' | 'scheduled' = saveAction === 'scheduled' ? 'scheduled' : 'draft';
      const scheduledAtVal =
        saveAction === 'scheduled'
          ? new Date(`${scheduledLaunchDate}T${scheduledLaunchTime}`).toISOString()
          : null;

      if (isEditMode && editCampaignId) {
        const { error: updErr } = await supabase
          .from('revenue_collection_campaigns')
          .update({
            period_month: periodMonth,
            period_year: periodYear,
            name: campaignName || null,
            status,
            notification_template_id: selectedInitialTemplateId,
            confirmation_template_id: selectedConfirmationTemplateId,
            scheduled_at: scheduledAtVal,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editCampaignId);

        if (updErr) throw updErr;

        const { error: delHotelsErr } = await supabase
          .from('revenue_collection_campaign_hotels')
          .delete()
          .eq('campaign_id', editCampaignId);

        if (delHotelsErr) throw delHotelsErr;

        const campaignHotels = Array.from(selectedHotelIds).map((hotel_id) => ({
          campaign_id: editCampaignId,
          hotel_id,
        }));

        const { error: insHotelsErr } = await supabase
          .from('revenue_collection_campaign_hotels')
          .insert(campaignHotels);

        if (insHotelsErr) throw insHotelsErr;

        const { error: delRemErr } = await supabase
          .from('revenue_collection_reminder_config')
          .delete()
          .eq('campaign_id', editCampaignId);

        if (delRemErr) throw delRemErr;

        for (let i = 0; i < reminderConfigs.length; i++) {
          const rc = reminderConfigs[i];
          if (rc.templateId && rc.days > 0) {
            await supabase.from('revenue_collection_reminder_config').insert({
              campaign_id: editCampaignId,
              reminder_template_id: rc.templateId,
              days_after_initial: rc.days,
              reminder_number: i + 1,
            });
          }
        }

        const path = `/revenue-collection/campaigns/${editCampaignId}`;
        navigate(saveAction === 'launch_now' ? `${path}?launch=true` : path);
        return;
      }

      const { data: campaign, error: campErr } = await supabase
        .from('revenue_collection_campaigns')
        .insert({
          period_month: periodMonth,
          period_year: periodYear,
          name: campaignName || null,
          status,
          notification_template_id: selectedInitialTemplateId,
          confirmation_template_id: selectedConfirmationTemplateId,
          scheduled_at: scheduledAtVal,
          created_by: user?.email,
        })
        .select('id')
        .single();

      if (campErr) throw campErr;
      if (!campaign) throw new Error('Failed to create campaign');

      const campaignHotels = Array.from(selectedHotelIds).map((hotel_id) => ({
        campaign_id: campaign.id,
        hotel_id,
      }));

      const { error: hotelsErr } = await supabase
        .from('revenue_collection_campaign_hotels')
        .insert(campaignHotels);

      if (hotelsErr) throw hotelsErr;

      for (let i = 0; i < reminderConfigs.length; i++) {
        const rc = reminderConfigs[i];
        if (rc.templateId && rc.days > 0) {
          await supabase.from('revenue_collection_reminder_config').insert({
            campaign_id: campaign.id,
            reminder_template_id: rc.templateId,
            days_after_initial: rc.days,
            reminder_number: i + 1,
          });
        }
      }

      const path = `/revenue-collection/campaigns/${campaign.id}`;
      navigate(saveAction === 'launch_now' ? `${path}?launch=true` : path);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create campaign');
    } finally {
      setSaving(false);
      setPendingSaveAction(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-[90rem] mx-auto py-6 sm:px-6 lg:px-8">
        <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
          <div className="flex flex-wrap items-center gap-4 min-w-0">
            <Link
              to={isEditMode && editCampaignId ? `/revenue-collection/campaigns/${editCampaignId}` : '/revenue-collection'}
              className="btn-icon-back"
              aria-label="Back"
            >
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <div className="flex flex-wrap items-center gap-3 min-w-0">
              <h1 className="page-title">
                {isEditMode ? 'Edit Collection Campaign' : 'New Collection Campaign'}
              </h1>
              <EmailTestModeBadge />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/revenue-collection/notifications"
              className="btn-secondary"
            >
              <Bell className="h-4 w-4 shrink-0" aria-hidden /> Notifications
            </Link>
            <Link
              to="/revenue-collection/templates"
              className="btn-secondary"
            >
              <FileText className="h-4 w-4 shrink-0" aria-hidden /> Templates
            </Link>
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" /> {error}
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Collection Period (Month)</label>
              <SingleSelectDropdown
                label="Month"
                options={MONTHS.map((_, i) => i + 1)}
                selected={periodMonth}
                onChange={(v) =>
                  setPeriodMonth(v === '' || v === undefined ? 1 : (typeof v === 'number' ? v : Number(v)) || 1)
                }
                renderOption={(m) => MONTHS[m - 1]}
                allowClear={false}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
              <SingleSelectDropdown
                label="Year"
                options={yearOptions}
                selected={periodYear}
                onChange={(v) =>
                  setPeriodYear(
                    v === '' || v === undefined ? currentYear : (typeof v === 'number' ? v : Number(v)) || currentYear
                  )
                }
                allowClear={false}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Name (optional)</label>
              <input
                type="text"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="e.g. Q1 2026 Collection"
                className={`w-full ${MP_FIELD}`}
              />
            </div>
          </div>

          <div>
            <div className="mb-2 flex flex-nowrap items-center justify-between gap-x-3 gap-y-2">
              <div className="flex min-w-0 flex-nowrap items-center gap-x-3">
                <span className="shrink-0 text-sm font-medium text-gray-700">Hotels</span>
                <div className="relative w-[14rem] max-w-[min(14rem,100%)] shrink-0">
                  <label htmlFor="hotel-search" className="sr-only">
                    Search hotels by name
                  </label>
                  <Search
                    className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
                    aria-hidden
                  />
                  <input
                    id="hotel-search"
                    type="search"
                    value={hotelSearchQuery}
                    onChange={(e) => setHotelSearchQuery(e.target.value)}
                    placeholder="Search by name…"
                    className="w-full rounded-md border border-gray-300 py-1.5 pl-7 pr-2 text-xs focus:border-action focus:outline-none focus:ring-1 focus:ring-action/40"
                  />
                </div>
              </div>
              <div className="flex shrink-0 gap-3">
                <button type="button" onClick={selectAll} className="btn-text-action">
                  Select all
                </button>
                <button type="button" onClick={deselectAll} className="btn-text-action">
                  Deselect all
                </button>
              </div>
            </div>
            {loading || loadingCampaign ? (
              <div className="text-gray-500">Loading hotels...</div>
            ) : hotelsWithRevenue.length === 0 ? (
              <div className="text-gray-500">No hotels with confirmed revenue for this period.</div>
            ) : hotelsFiltered.length === 0 ? (
              <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-600">
                {missingContactFilter && hotelSearchQuery.trim()
                  ? `No hotels without invoicing contact match “${hotelSearchQuery.trim()}”.`
                  : missingContactFilter
                    ? 'No hotels without invoicing contact for this period.'
                    : `No hotels match “${hotelSearchQuery.trim()}”. Try a different search.`}
              </div>
            ) : (
              <div className="border border-gray-200 rounded-md max-h-60 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 w-10" />
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Hotel</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Estimated Revenue</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Groups</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 min-w-[12rem]">
                        Invoicing contact
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {hotelsFiltered.map((h) => (
                      <tr key={h.id} className={!h.invoicing_contact?.trim() ? 'bg-amber-50' : ''}>
                        <td className="px-4 py-2">
                          <input
                            type="checkbox"
                            checked={selectedHotelIds.has(h.id)}
                            onChange={() => toggleHotel(h.id)}
                            className="rounded"
                          />
                        </td>
                        <td className="px-4 py-2 text-sm">{h.title}</td>
                        <td className="px-4 py-2 text-sm text-right">{formatCurrencyDecimals(h.estimated_revenue)}</td>
                        <td className="px-4 py-2 text-sm text-right">{h.groups_count}</td>
                        <td className="px-4 py-2 text-sm">
                          {h.invoicing_contact?.trim() ? (
                            <span className="text-action">{h.invoicing_contact.trim()}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {hotelsWithoutEmail.length > 0 && (
              <div className="mt-2 flex flex-col items-end gap-0.5 text-right">
                <button
                  type="button"
                  onClick={() => setMissingContactFilter((v) => !v)}
                  aria-pressed={missingContactFilter}
                  className={`text-sm text-amber-800 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50 rounded ${missingContactFilter ? 'font-semibold' : ''}`}
                >
                  {hotelsWithoutEmail.length} hotel(s) without invoicing contact (highlighted)
                  {missingContactFilter ? ' · Show all hotels' : ' · Filter list'}
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
              <div className="bg-white rounded-lg shadow border border-gray-100 p-4">
                <div className="text-sm text-gray-500">Total estimated revenue (period)</div>
                <div className="text-xl font-semibold mt-1">{formatCurrencyDecimals(totalPeriodEstimated)}</div>
              </div>
              <div className="bg-white rounded-lg shadow border border-gray-100 p-4">
                <div className="text-sm text-gray-500">Total estimated revenue (selected)</div>
                <div className="text-xl font-semibold mt-1">{formatCurrencyDecimals(totalEstimated)}</div>
              </div>
              <div className="bg-white rounded-lg shadow border border-gray-100 p-4">
                <div className="text-sm text-gray-500">Hotels selected</div>
                <div className="text-xl font-semibold mt-1 tabular-nums">
                  {hotelsSelectedCount}
                  <span className="text-gray-400 font-normal mx-1">/</span>
                  {hotelsWithRevenueCount}
                </div>
                <p className="text-xs text-gray-500 mt-1">Of hotels with confirmed revenue in this period</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Initial Notification Template</label>
            <SingleSelectDropdown
              label="Initial notification template"
              options={initialTemplates.map((t) => t.id)}
              selected={selectedInitialTemplateId}
              onChange={setSelectedInitialTemplateId}
              renderOption={(id) => initialTemplates.find((t) => t.id === id)?.name ?? String(id)}
              placeholder="Select..."
              allowClear={false}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Reminder Configuration</label>
            <div className="space-y-2">
              {reminderConfigs.map((rc, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <span className="w-24 shrink-0 text-sm">Reminder {i + 1}</span>
                  <SingleSelectDropdown
                    label={`Reminder ${i + 1} template`}
                    options={reminderTemplates.map((t) => t.id)}
                    selected={rc.templateId}
                    onChange={(id) => {
                      const next = [...reminderConfigs];
                      next[i] = { ...next[i], templateId: id };
                      setReminderConfigs(next);
                    }}
                    renderOption={(tid) => reminderTemplates.find((t) => t.id === tid)?.name ?? String(tid)}
                    placeholder="None"
                    className="min-w-0 flex-1"
                  />
                  <span className="text-sm">Days after initial:</span>
                  <input
                    type="number"
                    min={1}
                    value={rc.days}
                    onChange={(e) => {
                      const next = [...reminderConfigs];
                      next[i] = { ...next[i], days: Number(e.target.value) || 0 };
                      setReminderConfigs(next);
                    }}
                    className={`w-20 ${MP_FIELD}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Confirmation Template</label>
            <SingleSelectDropdown
              label="Confirmation template"
              options={confirmationTemplates.map((t) => t.id)}
              selected={selectedConfirmationTemplateId}
              onChange={setSelectedConfirmationTemplateId}
              renderOption={(id) => confirmationTemplates.find((t) => t.id === id)?.name ?? String(id)}
              placeholder="Select..."
              allowClear={false}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Scheduled launch</label>
            <p className="text-xs text-gray-500 mb-2">
              Used only when you choose <span className="font-medium text-gray-700">Save and launch later</span>. Leave
              date and time empty for draft or immediate launch. Time is limited to the hour or half past (:00 and :30).
              After saving, initial emails go out automatically after that time on the next automation run (every 30
              minutes). You can still open the campaign and press Launch to send immediately.
            </p>
            <div className="flex flex-wrap gap-4 items-end max-w-xl">
              <div className="min-w-[11rem] flex-1">
                <label htmlFor="scheduled-launch-date" className="block text-xs font-medium text-gray-600 mb-1">
                  Date
                </label>
                <input
                  id="scheduled-launch-date"
                  type="date"
                  value={scheduledLaunchDate}
                  onChange={(e) => setScheduledLaunchDate(e.target.value)}
                  className={`w-full ${MP_FIELD}`}
                />
              </div>
              <div className="min-w-[10rem] flex-1">
                <label htmlFor="scheduled-launch-time" className="block text-xs font-medium text-gray-600 mb-1">
                  Time
                </label>
                <select
                  id="scheduled-launch-time"
                  value={scheduledLaunchTime}
                  onChange={(e) => setScheduledLaunchTime(e.target.value)}
                  className={`w-full ${MP_FIELD}`}
                  aria-label="Scheduled launch time, half-hour slots only"
                >
                  <option value="">Select time…</option>
                  {SCHEDULE_LAUNCH_HALF_HOUR_OPTIONS.map((slot) => (
                    <option key={slot} value={slot}>
                      {slot}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
            <Link
              to={isEditMode && editCampaignId ? `/revenue-collection/campaigns/${editCampaignId}` : '/revenue-collection'}
              className="btn-teal-outline sm:order-first sm:mr-auto"
            >
              Cancel
            </Link>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              <button
                type="button"
                onClick={() => void handleSubmit('draft')}
                disabled={saving || loadingCampaign}
                className="btn-secondary !h-auto min-h-10 py-2 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4 shrink-0" aria-hidden />
                {saving && pendingSaveAction === 'draft'
                  ? isEditMode
                    ? 'Saving…'
                    : 'Creating…'
                  : 'Save as draft'}
              </button>
              <button
                type="button"
                onClick={() => void handleSubmit('scheduled')}
                disabled={saving || loadingCampaign}
                className="btn-scheduled !h-auto min-h-10 py-2 disabled:cursor-not-allowed"
              >
                <CalendarClock className="w-4 h-4 shrink-0" aria-hidden />
                {saving && pendingSaveAction === 'scheduled'
                  ? isEditMode
                    ? 'Saving…'
                    : 'Creating…'
                  : 'Save and launch later'}
              </button>
              <button
                type="button"
                onClick={() => void handleSubmit('launch_now')}
                disabled={saving || loadingCampaign}
                className="btn-primary !h-auto min-h-10 py-2 disabled:cursor-not-allowed disabled:hover:bg-action"
              >
                <Play className="w-4 h-4 shrink-0" aria-hidden />
                {saving && pendingSaveAction === 'launch_now'
                  ? isEditMode
                    ? 'Saving…'
                    : 'Creating…'
                  : 'Save and launch'}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
